import { BoxShadowWithRadius } from 'accessibility/focusStyles'
import classnames from 'classnames'
import EmojiPicker from 'components/EmojiPicker'
import GiphyPicker from 'components/GiphyPicker'
import { Close, Download, Emoji } from 'components/lib/icons'
import Send from 'components/lib/icons/Send'
import NakedButton from 'components/lib/NakedButton'
import LoaderBalls from 'components/LoaderBalls'
import { useActiveSenderId } from 'hooks/useActiveSenderId'
import useAttachmentUpload from 'hooks/useAttachmentUpload'
import useComposerState from 'hooks/useComposerState'
import useIsMobile from 'hooks/useIsMobile'
import useKeyboardNavigation from 'hooks/useKeyboardNavigation'
import usePasteFiles from 'hooks/usePasteFiles'
import { useRecipientResolution } from 'hooks/useRecipientResolution'
import useThemeColors from 'hooks/useThemeColors'
import useTypingIndicators from 'hooks/useTypingIndicators'
import { isConversationIdNullOrPreRendered } from 'modules/conversations/utils'
import { getEndUserAllowedToSendGif } from 'modules/embed/selectors'
import { createMessage } from 'modules/message/creators'
import { pathOr, without } from 'ramda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ATTACHMENT_STATES } from 'types/attachments'
import { ConversationId } from 'types/conversation'
import { isNilOrEmpty, onNextTick, sanitizeText } from 'utils'

import { AttachmentIconButton, FileAttachmentErrorBanner } from './FileAttachment'

import './style.css'

const COMPOSER_MESSAGE_TYPE = {
  GIF: "GIF",
  TEXT: "TEXT"
}

const validateMessageText = text => !isNilOrEmpty(text) && text.trim().length > 0
const resolveAttachmentTypeComponent = file => {
  if (!file) { return null }

  if (!file.mimeType.includes('image')) {
    return (
      <div className="drift-widget-composer-attachment-non-image" title={file.filename}>
        <strong>{file.fileName}</strong>
        <Download width={16} height={16} aria-hidden={true} />
      </div>
    )
  }

  return (
    <div className="drift-widget-composer-attachment-preview-list-image-wrapper">
      <img alt={file.fileName} src={file.publicUrl} />
    </div>
  )
}

export enum ComposerState {
  AVAILABLE,
  AVAILABLE_BUTTONS,
  UNAVAILABLE,
  UNAVAILABLE_BUTTONS
}

interface ComposerProps {
  onSubmit: Function,
  activeConversationId: ConversationId,
  disabled?: boolean,
  state?: ComposerState,
  chatHidden?: boolean,
  alwaysShowSendAction?: boolean,
  useGifPicker?: boolean,
  alwaysShow?: boolean
}

const Composer = ({
  onSubmit,
  activeConversationId,
  disabled = false,
  chatHidden,
  alwaysShowSendAction = false,
  useGifPicker = true,
  alwaysShow = false
}: ComposerProps) => {
  // lib hooks
  const textareaRef = useRef(null)
  const sendButtonRef = useRef(null)
  const composerState = useComposerState()

  const [message, setMessage] = useState('')
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)

  const isInputKeyboardFocused = useKeyboardNavigation(textareaRef)

  const [uploadedAttachments, setUploadedAttachments] = useState([])

  //selectors
  const { matchedHours, chatOpen } = useSelector(state => state.view)
  const attachments = useSelector(state => state.conversations.attachments)

  const endUserId = useSelector(state => (
    state.session.endUser.id
  ))

  const { allowEndUserFileUpload } = useSelector(state =>
    pathOr(false, ['embed', 'configuration', 'theme'], state)
  )

  const isAllowedToSendGif = useSelector(getEndUserAllowedToSendGif)
  const gifsEnabled = useGifPicker && isAllowedToSendGif

  //custom hooks
  const { t } = useTranslation()
  const isMobile = useIsMobile(true)
  const activeSenderId = useActiveSenderId()
  const { activeColor, composerTheme, composerButtonHoverColor } = useThemeColors()
  const { recipient } = useRecipientResolution(activeSenderId)
  const { pastedFiles, clearFiles } = usePasteFiles({ inputRef: textareaRef })

  const {
    sendTypingStarted,
    sendTypingFinished
  } = useTypingIndicators({ activeConversationId })

  const {
    attachmentState,
    uploadAttachments,
    resetAttachmentState
  } = useAttachmentUpload({
    activeConversation: activeConversationId,
    onAttachmentUploadComplete: useCallback(onAttachmentUploadComplete, [])
  })

  const isAvailable = composerState === ComposerState.AVAILABLE || composerState === ComposerState.AVAILABLE_BUTTONS

  const isComposerHidden = useMemo(() => {
    if (alwaysShow) {
      return false
    }

    return (disabled || !isAvailable)
  }, [alwaysShow, disabled, isAvailable])

  const {
    getRootProps,
    getInputProps,
    open
  } = useDropzone({
    onDrop: uploadAttachments,
    disabled: disabled || !isAvailable || !allowEndUserFileUpload
  })


  //derived vars
  const hasMessage = validateMessageText(message) || !isNilOrEmpty(uploadedAttachments)
  const attachmentUploadPending = attachmentState === ATTACHMENT_STATES.PENDING
  const attachmentError = attachmentState === ATTACHMENT_STATES.ERROR || attachmentState === ATTACHMENT_STATES.SIZE_ERROR
  const isComposerDisabled = disabled || !isAvailable || !!pastedFiles.length

  //handlers
  function onAttachmentUploadComplete(files) {
    setUploadedAttachments(prevAttachments => {
      return [...prevAttachments, ...files]
    })
    clearFiles()
  }

  const onRemoveUploadedAttachment = attachmentId => {
    setUploadedAttachments(prevAttachments => {
      return without([attachmentId], prevAttachments)
    })
  }

  const onTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (pastedFiles.length) {
      return
    }

    setMessage(event.target.value)
    sendTypingStarted()
  }

  const handleTextareaKeydown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' || event.keyCode === 13) {
      event.preventDefault() // prevents new lines being added in textarea when hitting enter
      if (validateMessageText(message) || uploadedAttachments.length) {
        submitMessage(message, COMPOSER_MESSAGE_TYPE.TEXT)
        setMessage('')
      }
    }
  }

  const onAddEmoji = (emoji) => {
    setEmojiPickerOpen(!emojiPickerOpen)
    if (isAvailable && !disabled) {
      setMessage(emoji)
      textareaRef.current && textareaRef.current.focus()
    }
  }

  const onEmojiButtonClick = () => {
    if (isComposerDisabled) {
      return
    }
    setEmojiPickerOpen(!emojiPickerOpen)
  }

  const onEmojiPickerClickOutside = () => {
    setEmojiPickerOpen(false)
  }

  const setFocusOnComposer = () => {
    textareaRef.current.focus()
    textareaRef.current.classList.remove('focus-visible')
  }

  const submitMessage = useCallback((messageToSend, messageType = COMPOSER_MESSAGE_TYPE.TEXT) => {
    const attachments = uploadedAttachments
      ? { attachments: uploadedAttachments }
      : {}

    const attributes = !activeConversationId
      ? { sentWelcomeMessage: matchedHours }
      : {}

    sendTypingFinished()

    const sanitizedMessageToSend = messageType === COMPOSER_MESSAGE_TYPE.TEXT
      ? sanitizeText(messageToSend)
      : messageToSend

    onSubmit({
      message: createMessage({
        message: sanitizedMessageToSend,
        activeConversation: activeConversationId || null,
        authorId: endUserId,
        attributes,
        ...attachments
      })
    })

    setMessage('')
    setUploadedAttachments([])
    resetAttachmentState()
    setFocusOnComposer()
  }, [activeConversationId, endUserId, matchedHours, onSubmit, resetAttachmentState, sendTypingFinished, uploadedAttachments])

  //effects
  useEffect(() => {
    if (!allowEndUserFileUpload) {
      clearFiles()
      return
    }

    if (pastedFiles.length && !attachmentUploadPending) {
      uploadAttachments(pastedFiles)
    }
  }, [allowEndUserFileUpload, attachmentUploadPending, clearFiles, pastedFiles, uploadAttachments])

  useEffect(() => {
    if (activeConversationId) {
      setMessage('')
    }
  }, [activeConversationId])

  useEffect(() => {
    const composerRef = textareaRef
    const shouldFocus = chatOpen && isAvailable && !chatHidden

    if (shouldFocus) {
      onNextTick(() => composerRef?.current?.focus())
    }

    return () => composerRef?.current?.blur()
  }, [chatHidden, chatOpen, isAvailable, composerState])

  const placeholderText = useMemo(() => (
    !isAvailable ?
      t(composerState === ComposerState.UNAVAILABLE_BUTTONS ? 'composer.chooseOptionAbove' : 'composer.replyDisabled') :
      t('composer.replyToPlaceholder', { name: recipient.name })
  ), [isAvailable, recipient.name, composerState, t])

  const showAttachmentAction = allowEndUserFileUpload && !isConversationIdNullOrPreRendered(activeConversationId)
  const showGifAction = gifsEnabled && !hasMessage && !!activeConversationId
  const showSendAction = alwaysShowSendAction || !gifsEnabled || hasMessage
  const showActions = showAttachmentAction || showSendAction || showGifAction
  const sendButtonDisabled = disabled || !hasMessage || attachmentUploadPending

  return (
    <>
      {(uploadedAttachments.length || attachmentUploadPending || attachmentError) && (
        <div className="drift-widget-composer--pre-message-attachments">
          <style>
            {`
                .drift-widget-composer--pre-message-attachments button:focus {
                  ${BoxShadowWithRadius(activeColor)}
                }
            `}
          </style>
          {attachmentError && (
            <FileAttachmentErrorBanner
              attachmentState={attachmentState}
              clearFiles={clearFiles}
              disabled={disabled}
              reset={resetAttachmentState}
            />
          )}
          {(uploadedAttachments.length || attachmentUploadPending) && (
            <ul className="drift-widget-composer-attachment-preview-list">
              {
                [...uploadedAttachments,
                ...(attachmentUploadPending ? [{ pending: true }] : [])
                ]
                  .map((file, index) => (
                    <li key={attachments[file] ? attachments[file].id : `file-upload-loader`}>
                      {(attachments[file] && !attachments.pending) &&
                        <NakedButton
                          aria-label={`Remove attachment ${index + 1}`}
                          onClick={() => onRemoveUploadedAttachment(file)}
                          disabled={disabled}
                        >
                          <Close width={6} height={6} />
                        </NakedButton>
                      }
                      {(attachments[file] && !attachments.pending)
                        ? resolveAttachmentTypeComponent(attachments[file])
                        : <LoaderBalls size="small" className="drift-widget-composer-attachment-loader" />
                      }
                    </li>
                  ))
              }
            </ul>
          )}
        </div>
      )}
      <style>
        {`
          .drift-widget-composer--default button:hover > svg path {
            fill: ${composerButtonHoverColor}
          }
        `}
      </style>
      <div
        {...getRootProps({ onClick: event => event.stopPropagation() })}
        className={classnames("drift-widget-composer--default", {
          'drift-widget-composer--mobile': isMobile,
          "drift-widget-composer--hidden": isComposerHidden
        })}
        tabIndex={undefined}
      >
        {allowEndUserFileUpload && <input {...getInputProps()} />}

        <div className="drift-widget-composer--text-area">
          <pre>{isAvailable && message.length ? message : ' '}</pre>
          <label className="drift-a11y-visually-hidden" htmlFor="drift-widget-composer-input">{placeholderText}</label>
          <textarea
            className={classnames("drift-widget-input js-focus-visible--controlled", {
              'keyboard-focused': isInputKeyboardFocused
            })}
            ref={textareaRef}
            placeholder={placeholderText}
            value={isAvailable ? message : ''}
            onChange={onTextareaChange}
            onKeyDown={handleTextareaKeydown}
            aria-label="message composer"
            maxLength={4096}
            disabled={isComposerDisabled}
            id="drift-widget-composer-input"
            style={composerTheme}
          />
          {isAvailable && !isMobile &&
            <button
              className="drift-widget-composer-emoji-toggle"
              onClick={onEmojiButtonClick}
              disabled={disabled}
              aria-pressed={emojiPickerOpen}
              aria-label={`Click to ${emojiPickerOpen ? 'close' : 'open'} emoji picker`}
            >
              <Emoji />
            </button>
          }
        </div>

        {isAvailable && !isMobile &&
          <EmojiPicker
            open={emojiPickerOpen}
            onAddEmoji={onAddEmoji}
            onClickOutside={onEmojiPickerClickOutside}
          />
        }

        {isAvailable && showActions &&
          <div className={classnames("drift-widget-composer-actions", {
            "drift-widget-composer-actions-expanded": showAttachmentAction && (showGifAction || showSendAction)
          })}>
            {showAttachmentAction &&
              <AttachmentIconButton
                isDisabled={disabled}
                open={open}
              />
            }

            {showGifAction &&
              <GiphyPicker
                onAddGiphy={(message) => submitMessage(message, COMPOSER_MESSAGE_TYPE.GIF)}
                disabled={disabled}
              />
            }

            {showSendAction &&
              <button
                className={classnames("drift-widget-composer-send-button", {
                  "drift-widget-composer-send-button--disabled": sendButtonDisabled,
                })}
                aria-label="send message"
                onClick={() => submitMessage(message, COMPOSER_MESSAGE_TYPE.TEXT)}
                disabled={sendButtonDisabled || attachmentUploadPending}
                ref={sendButtonRef}
              >
                <Send color={`#${activeColor}`} />
              </button>
            }
          </div>
        }
      </div>
    </>
  )
}

export default Composer
